import { css } from 'aphrodite';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import AudioPlayer, { RHAP_UI } from 'react-h5-audio-player';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { copyToClipboard } from '../../../models/UiUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { white } from '../../styles/colors';
import { ForwardIcon } from '../svgs/icons/audio/ForwardIcon';
import { PauseIcon } from '../svgs/icons/audio/PauseIcon';
import { PlayIcon } from '../svgs/icons/audio/PlayIcon';
import { RewindIcon } from '../svgs/icons/audio/RewindIcon';
import { VolumeIcon } from '../svgs/icons/audio/VolumeIcon';
import { VolumeMuteIcon } from '../svgs/icons/audio/VolumeMute';
import { CopyIcon } from '../svgs/icons/CopyIcon';
import { TransparentButton } from '../TransparentButton';
import { ApiClient } from '../../services/api';
import { RecordingPlaybackReporter, RecordingPlaybackSurface } from '../../services/recordingPlayback';
import './audioPlayer.less';
import { styleSheet } from './styles';

interface IProps {
	autoplay: boolean;
	phoneCallId: string;
	source: string;
	subtitle: string;
	surface: RecordingPlaybackSurface;
	title: string;
}

const PLAYBACK_SPEED_STORAGE_KEY = 'customAudioPlayer_playbackSpeed';

const getStoredPlaybackSpeed = (): number => {
	try {
		const stored = localStorage.getItem(PLAYBACK_SPEED_STORAGE_KEY);
		if (stored) {
			const speed = parseFloat(stored);
			// Validate that it's one of our supported speeds
			const supportedSpeeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
			return supportedSpeeds.includes(speed) ? speed : 1;
		}
	} catch (error) {
		// localStorage might not be available (e.g., in incognito mode)
		console.warn('Failed to read playback speed from localStorage:', error);
	}
	return 1; // default
};

const setStoredPlaybackSpeed = (speed: number): void => {
	try {
		localStorage.setItem(PLAYBACK_SPEED_STORAGE_KEY, speed.toString());
	} catch (error) {
		console.warn('Failed to save playback speed to localStorage:', error);
	}
};

export const CustomAudioPlayer: React.FC<IProps> = ({
	autoplay = false,
	phoneCallId,
	source,
	subtitle,
	surface,
	title,
}) => {
	const userSession = useUserSession();
	const audioPlayerRef = useRef<AudioPlayer>(null);
	const [playbackSpeed, setPlaybackSpeed] = useState<number>(getStoredPlaybackSpeed());
	const playbackReporterRef = useRef<RecordingPlaybackReporter>(null);
	if (!playbackReporterRef.current) {
		playbackReporterRef.current = new RecordingPlaybackReporter(new ApiClient(userSession));
	}
	const playbackReporter = playbackReporterRef.current;

	const reportPlay = () => {
		const element = audioPlayerRef.current?.audio?.current;
		playbackReporter.reportPlay({
			phoneCallId,
			playbackRate: element?.playbackRate,
			positionSeconds: element?.currentTime,
			surface,
		});
	};

	const copyAudioLink = (evt: React.MouseEvent<HTMLSpanElement>) => {
		evt.stopPropagation();
		const url = `${userSession.webServiceHelper.baseUrl}/PhoneCall/${phoneCallId}/Download`;
		if (copyToClipboard(url)) {
			toast.success('Link copied to clipboard');
		} else {
			toast.error('Email failed to copy to clipboard');
		}
	};

	const handleSpeedChange = (speed: number) => {
		setPlaybackSpeed(speed);
		setStoredPlaybackSpeed(speed);
		if (audioPlayerRef.current?.audio?.current) {
			audioPlayerRef.current.audio.current.playbackRate = speed;
		}
	};

	// Apply stored playback speed when audio is loaded
	useEffect(() => {
		if (audioPlayerRef.current?.audio?.current) {
			audioPlayerRef.current.audio.current.playbackRate = playbackSpeed;
		}
	}, [source, playbackSpeed]);

	const SpeedControl = () => {
		const speeds = [
			{ value: '0.5', label: '0.5x' },
			{ value: '0.75', label: '0.75x' },
			{ value: '1', label: 'Normal' },
			{ value: '1.25', label: '1.25x' },
			{ value: '1.5', label: '1.5x' },
			{ value: '2', label: '2x' },
		];

		return (
			<div className={css(styleSheet.speedControl)}>
				<label className={css(styleSheet.speedLabel)}>Playback:</label>
				<Select
					items={speeds}
					value={playbackSpeed.toString()}
					onValueChange={value => handleSpeedChange(parseFloat(value))}
				>
					<SelectTrigger styles={styleSheet.speedSelect}>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{speeds.map(speed => (
							<SelectItem key={speed.value} value={speed.value}>
								{speed.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
		);
	};

	const renderHeader = (
		<div className={css(styleSheet.header)}>
			<div className={css(styleSheet.flexBetween)}>
				<div className={css(styleSheet.titleBar)}>
					<div className={css(styleSheet.titleContainer)}>
						<div className={css(styleSheet.title)}>
							{title}
							<TransparentButton onClick={copyAudioLink} className={css(styleSheet.copyButton)}>
								<CopyIcon fillColor={white} className={css(styleSheet.copyIcon)} />
							</TransparentButton>
						</div>
						<div className={css(styleSheet.subtitle)}>{subtitle}</div>
					</div>
				</div>
			</div>
		</div>
	);

	const iconSet = useRef({
		forward: <ForwardIcon />,
		pause: <PauseIcon />,
		play: <PlayIcon />,
		rewind: <RewindIcon />,
		volume: <VolumeIcon />,
		volumeMute: <VolumeMuteIcon />,
	}).current;

	return (
		<div className={css(styleSheet.container)}>
			{renderHeader}
			<AudioPlayer
				ref={audioPlayerRef}
				className={css(styleSheet.playerWrapper)}
				customAdditionalControls={[]}
				customControlsSection={[RHAP_UI.MAIN_CONTROLS, RHAP_UI.VOLUME_CONTROLS]}
				src={source}
				customIcons={iconSet}
				layout='horizontal'
				autoPlay={autoplay}
				onPlay={reportPlay}
				showSkipControls={false}
				showJumpControls={false}
			/>
			<div className={css(styleSheet.speedControlSection)}>
				<SpeedControl />
			</div>
		</div>
	);
};
